import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { AlertCircle } from 'lucide-react';
import type { HDType } from '@/types/user';

interface RegisterForm {
  email: string;
  password: string;
  displayName: string;
  energyType: HDType;
}

export default function Register() {
  const { register: registerAuth } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language } = useLanguage();
  const { register, handleSubmit, formState: { errors, isSubmitting }, setError } = useForm<RegisterForm>();

  const onSubmit = async (data: RegisterForm) => {
    try {
      await registerAuth(data.email, data.password, data.displayName, data.energyType);
      
      toast({
        title: language === 'fr' ? 'Compte créé avec succès' : 'Account created successfully',
        description: language === 'fr'
          ? 'Vous pouvez maintenant vous connecter.'
          : 'You can now log in.',
      });
      navigate('/barometre/login');
    } catch (error: any) {
      // Gérer spécifiquement l'erreur "utilisateur déjà existant"
      if (error.message.includes('Un compte existe déjà') || error.message.includes('already exists')) {
        setError('email', {
          type: 'manual',
          message: language === 'fr'
            ? 'Un compte existe déjà avec cette adresse email'
            : 'An account already exists with this email address'
        });
        return;
      }
      
      // Gérer les autres erreurs
      toast({
        variant: "destructive",
        title: language === 'fr' ? "Erreur d'inscription" : "Registration Error",
        description: error.message || (language === 'fr'
          ? "Une erreur est survenue lors de l'inscription"
          : "An error occurred during registration"),
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-2xl font-bold text-center">
            {language === 'fr' ? 'Inscription' : 'Register'}
          </h1>
          <p className="text-sm text-gray-500 text-center">
            {language === 'fr'
              ? 'Créez votre compte pour commencer votre voyage énergétique'
              : 'Create your account to start your energy journey'}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {language === 'fr' ? 'Nom d\'utilisateur' : 'Username'}
              </label>
              <input
                type="text"
                {...register('displayName', {
                  required: language === 'fr'
                    ? 'Le nom d\'utilisateur est requis'
                    : 'Username is required'
                })}
                className={`mt-1 block w-full rounded-md shadow-sm focus:border-purple-500 focus:ring-purple-500 ${
                  errors.displayName ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.displayName && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.displayName.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                {...register('email', { 
                  required: language === 'fr' ? 'Email requis' : 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: language === 'fr'
                      ? "Adresse email invalide"
                      : "Invalid email address"
                  }
                })}
                className={`mt-1 block w-full rounded-md shadow-sm focus:border-purple-500 focus:ring-purple-500 ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {language === 'fr' ? 'Mot de passe' : 'Password'}
              </label>
              <input
                type="password"
                {...register('password', { 
                  required: language === 'fr' ? 'Mot de passe requis' : 'Password is required',
                  minLength: { 
                    value: 6, 
                    message: language === 'fr'
                      ? 'Le mot de passe doit contenir au moins 6 caractères'
                      : 'Password must be at least 6 characters long'
                  }
                })}
                className={`mt-1 block w-full rounded-md shadow-sm focus:border-purple-500 focus:ring-purple-500 ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.password.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {language === 'fr' ? 'Type d\'énergie' : 'Energy Type'}
              </label>
              <select
                {...register('energyType', {
                  required: language === 'fr'
                    ? 'Type d\'énergie requis'
                    : 'Energy type is required'
                })}
                className={`mt-1 block w-full rounded-md shadow-sm focus:border-purple-500 focus:ring-purple-500 ${
                  errors.energyType ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">
                  {language === 'fr' ? 'Sélectionnez votre type' : 'Select your type'}
                </option>
                <option value="manifestor">
                  {language === 'fr' ? 'Manifesteur' : 'Manifestor'}
                </option>
                <option value="generator">
                  {language === 'fr' ? 'Générateur' : 'Generator'}
                </option>
                <option value="manifesting-generator">
                  {language === 'fr' ? 'Générateur Manifestant' : 'Manifesting Generator'}
                </option>
                <option value="projector">
                  {language === 'fr' ? 'Projecteur' : 'Projector'}
                </option>
                <option value="reflector">
                  {language === 'fr' ? 'Réflecteur' : 'Reflector'}
                </option>
              </select>
              {errors.energyType && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.energyType.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-[#54075B] hover:bg-[#54075B]/90"
              disabled={isSubmitting}
            >
              {isSubmitting 
                ? (language === 'fr' ? 'Inscription...' : 'Registering...')
                : (language === 'fr' ? 'S\'inscrire' : 'Register')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}